import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const MarcarAsistenciaQR = () => {
  const { user, API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [clase, setClase] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [marcando, setMarcando] = useState(false);

  const clase_id = searchParams.get('clase_id');

  useEffect(() => {
    // Verificar si el usuario está logueado
    if (!user) {
      // Guardar la URL actual para redirigir después del login
      sessionStorage.setItem('redirect_after_login', window.location.pathname + window.location.search);
      navigate('/app');
      return;
    }

    // Verificar que haya clase_id en la URL
    if (!clase_id) {
      setError('No se especificó la clase. Por favor escanea el código QR nuevamente.');
      setLoading(false);
      return;
    }

    // Cargar información de la clase
    loadClase();
  }, [user, clase_id, navigate]);

  const loadClase = async () => {
    try {
      const response = await axios.get(`${API}/clases`);
      const clases = response.data;
      const claseEncontrada = clases.find(c => c.id === clase_id);
      
      if (!claseEncontrada) {
        setError('Clase no encontrada. El código QR podría ser inválido.');
        setLoading(false);
        return;
      }

      setClase(claseEncontrada);
      setLoading(false);
    } catch (err) {
      console.error('Error loading clase:', err);
      setError('Error al cargar la información de la clase.');
      setLoading(false);
    }
  };

  const marcarAsistencia = async () => {
    if (!clase_id) return;

    setMarcando(true);
    try {
      const response = await axios.post(`${API}/clases/marcar-asistencia-qr?clase_id=${clase_id}`);
      
      if (response.data.success) {
        setSuccess(true);
        toast.success('¡Asistencia marcada exitosamente!');
      }
    } catch (err) {
      console.error('Error marking attendance:', err);
      const errorMsg = err.response?.data?.detail || 'Error al marcar asistencia';
      toast.error(errorMsg);
      setError(errorMsg);
    } finally {
      setMarcando(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-pink-50">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="p-8 text-center">
            <i className="fas fa-spinner fa-spin text-4xl text-purple-600 mb-4"></i>
            <p className="text-gray-600">Cargando información...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error && !clase) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-center text-red-700 flex items-center justify-center">
              <i className="fas fa-exclamation-triangle mr-2"></i>
              Error
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-gray-700">{error}</p>
            <Button 
              className="w-full bg-blue-600 hover:bg-blue-700"
              onClick={() => navigate('/clases')}
            >
              <i className="fas fa-arrow-left mr-2"></i>
              Volver a Clases
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-center text-green-700 flex items-center justify-center">
              <i className="fas fa-check-circle text-3xl mr-2"></i>
              ¡Asistencia Confirmada!
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Información de la clase */}
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <div className="text-center">
                <i className="fas fa-graduation-cap text-3xl text-green-600 mb-3"></i>
                <h3 className="font-semibold text-gray-900 text-lg mb-1">
                  {clase?.titulo}
                </h3>
                <p className="text-sm text-gray-600">
                  {clase?.categoria === 'estudiantes' ? 'Para Estudiantes' : 'Para Profesores'}
                </p>
              </div>
            </div>

            {/* Mensaje de éxito */}
            <div className="bg-white p-4 rounded-lg border-2 border-green-300">
              <div className="text-center space-y-2">
                <p className="text-green-800 font-semibold">
                  Tu asistencia ha sido registrada correctamente
                </p>
                <p className="text-sm text-gray-600">
                  <i className="fas fa-clock mr-1"></i>
                  {new Date().toLocaleString('es-UY')}
                </p>
              </div>
            </div>

            {/* Información adicional */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800 text-center">
                <i className="fas fa-info-circle mr-1"></i>
                Puedes cerrar esta ventana o volver a la página de clases
              </p>
            </div>

            {/* Botones de acción */}
            <div className="space-y-2">
              <Button 
                className="w-full bg-blue-600 hover:bg-blue-700"
                onClick={() => navigate('/clases')}
              >
                <i className="fas fa-arrow-left mr-2"></i>
                Volver a Clases
              </Button>
              
              <Button 
                variant="outline"
                className="w-full"
                onClick={() => window.close()}
              >
                <i className="fas fa-times mr-2"></i>
                Cerrar esta ventana
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ¿Hoy es día de clase?
  const diasSemanaMap = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
  const normalizar = (d) => (d || '').toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const hoyDia = normalizar(diasSemanaMap[new Date().getDay()]);
  const diasClase = (() => {
    if (!clase) return [];
    if (Array.isArray(clase.dias_semana)) return clase.dias_semana.map(normalizar);
    if (typeof clase.dia_semana === 'string') return clase.dia_semana.split(',').map(s => normalizar(s.trim())).filter(Boolean);
    return [];
  })();
  const esHoyDiaDeClase = diasClase.length === 0 ? true : diasClase.includes(hoyDia);
  const nombreDiaHoy = diasSemanaMap[new Date().getDay()];
  const diasClaseTexto = diasClase.length ? diasClase.map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(', ') : '';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-pink-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-center text-purple-700 flex items-center justify-center">
            <i className="fas fa-qrcode text-2xl mr-2"></i>
            Marcar Asistencia
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Información de la clase */}
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <div className="text-center">
              <i className="fas fa-graduation-cap text-3xl text-purple-600 mb-3"></i>
              <h3 className="font-semibold text-gray-900 text-lg mb-1">
                {clase?.titulo}
              </h3>
              <p className="text-sm text-gray-600 mb-2">
                {clase?.categoria === 'estudiantes' ? 'Para Estudiantes' : 'Para Profesores'}
              </p>
              {clase?.profesor_nombre && (
                <p className="text-sm text-gray-600">
                  <i className="fas fa-chalkboard-teacher mr-1"></i>
                  {clase.profesor_nombre}
                </p>
              )}
            </div>
          </div>

          {/* Información del usuario */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800 text-center mb-2">
              <i className="fas fa-user mr-1"></i>
              Marcando asistencia como:
            </p>
            <p className="font-semibold text-gray-900 text-center">
              {user?.nombre} {user?.apellido}
            </p>
            <p className="text-xs text-gray-600 text-center">{user?.email}</p>
          </div>

          {/* Instrucciones */}
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <h4 className="font-semibold text-yellow-800 mb-2 text-sm">
              <i className="fas fa-info-circle mr-1"></i>
              Importante:
            </h4>
            <ul className="text-xs text-yellow-700 space-y-1">
              <li>• Solo puedes marcar asistencia el día de la clase</li>
              <li>• Debes estar inscrito en esta clase</li>
              <li>• La marca es única (no se puede repetir)</li>
              <li>• Se registrará tu asistencia presencial</li>
            </ul>
          </div>

          {/* Aviso si HOY no es día de clase — bloquea el botón */}
          {!esHoyDiaDeClase && diasClaseTexto && (
            <div className="bg-orange-50 p-3 rounded-lg border border-orange-200" data-testid="qr-no-dia-clase-alert">
              <p className="text-sm text-orange-800 text-center">
                <i className="fas fa-calendar-times mr-1"></i>
                <strong>Hoy es {nombreDiaHoy.charAt(0).toUpperCase() + nombreDiaHoy.slice(1)}.</strong><br />
                Esta clase se dicta los <strong>{diasClaseTexto}</strong>.<br />
                Solo podrás marcar asistencia esos días.
              </p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 p-3 rounded-lg border border-red-200">
              <p className="text-sm text-red-700 text-center">
                <i className="fas fa-exclamation-triangle mr-1"></i>
                {error}
              </p>
            </div>
          )}

          {/* Botón de marcar asistencia */}
          <Button 
            className="w-full bg-purple-600 hover:bg-purple-700 text-white py-6 text-lg font-semibold shadow-lg disabled:bg-gray-300 disabled:cursor-not-allowed"
            onClick={marcarAsistencia}
            disabled={marcando || !esHoyDiaDeClase}
            data-testid="qr-marcar-asistencia-btn"
          >
            {marcando ? (
              <>
                <i className="fas fa-spinner fa-spin mr-2"></i>
                Marcando asistencia...
              </>
            ) : (
              <>
                <i className="fas fa-check-circle mr-2"></i>
                Marcar mi asistencia
              </>
            )}
          </Button>

          {/* Botón cancelar */}
          <Button 
            variant="outline"
            className="w-full"
            onClick={() => navigate('/clases')}
          >
            <i className="fas fa-times mr-2"></i>
            Cancelar
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default MarcarAsistenciaQR;
